import type { OrderRequestPayment } from '@/types/order.types';

/**
 * DV-2804: single source of truth for the order payment object
 * (`{ tax, customerProfileId, customerPaymentProfileId }`). It was hand-built in
 * four places (PaymentPage, usePayment.stampCardOnBulkOrder,
 * orderServicePayloads.buildAssetOrder, useBulkImportOrder), so DV-2789 had to
 * edit all four in lockstep to stop leaking the CVV — and missing one would have
 * kept a CVV leaking from that flow with no compile error. Centralise the shape
 * so the next payment-field change is a one-line edit.
 *
 * Accepts a nullable card (PaymentPage's `selectedCard` is optional) and falls
 * back to empty ids, preserving the prior `?? ''` behaviour at that call site.
 * The bulk-import flow carries `cardNumber`/`cardType` alongside these fields at
 * the top level, so it spreads this result and adds those two.
 *
 * KNOWN HOLDOUT (DV-2804 review): `cartTransforms.transformBulkOrderCartData`
 * builds a *different* per-order payment placeholder — `{ customerProfileId,
 * customerPaymentProfileId, cardNumber, cardType }` all empty, nested and with no
 * `tax` — that also ships to order-service. It is deliberately NOT routed through
 * this helper (different shape). If you change the payment wire shape here, update
 * that site too — it won't fail to compile.
 */
export function buildOrderPayment(
  card:
    | {
        customerProfileId?: string | null;
        customerPaymentProfileId?: string | null;
      }
    | null
    | undefined,
): OrderRequestPayment {
  return {
    tax: 0,
    customerProfileId: card?.customerProfileId ?? '',
    customerPaymentProfileId: card?.customerPaymentProfileId ?? '',
  };
}
